"use client";

import { useEffect, useRef, useState } from "react";

export function useScrollAnimate() {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Guard for environments without IntersectionObserver
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setIsVisible(true);
      return;
    }

    // Cleanup existing observer if any
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    // Only create observer if not already visible
    if (!isVisible) {
      const observer = new IntersectionObserver(
        (entries) => {
          // Since we only observe one element, entries will only contain one entry
          const entry = entries[0];
          if (entry?.isIntersecting) {
            setIsVisible(true);
            // Disconnect observer once visible to avoid unnecessary observation
            observer.disconnect();
            observerRef.current = null;
          }
        },
        {
          threshold: 0.15,
          rootMargin: "0px 0px -10% 0px",
        }
      );

      observerRef.current = observer;
      observer.observe(element);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [isVisible]);

  return { ref, isVisible };
}
